import {
    ChangeDetectorRef,
    ContentChild,
    Directive,
    ElementRef,
    EventEmitter, Injector,
    Input,
    OnInit,
    Optional,
    Output,
    TemplateRef,
    ViewChild
} from "@angular/core";
import {CellActivationMode, EditGridService} from "../edit/edit-grid/edit-grid.service";
import {ControlContainer, FormGroup} from "@angular/forms";
import {DataResourceModel, SearchModel} from "@fedeltapointofsale/data-client";
import {EditService} from "../edit/edit-page/edit.service";
import {EDIT_CONTROL_LABEL_CONTEXT} from "../edit/edit-controls/abstract-edit-control.component";

/**
 * Directive to define a template for a cell
 */
@Directive({selector: "[cellTemplate]"})
export class CellTemplate {
}

/**
 * Directive to define a template for a linking
 */
@Directive({selector: "[linkTemplate]"})
export class LinkTemplate {
}

/**
 * Directive to define a template for a header
 */
@Directive({selector: "[headerTemplate]"})
export class HeaderTemplate {
}

/**
 * Directive to define a template for a footer
 */
@Directive({selector: "[footerTemplate]"})
export class FooterTemplate {
}

/**
 * Directive to define a template for editing
 */
@Directive({selector: "[editTemplate]"})
export class EditTemplate {
}

export interface ActionEvent {
}

/**
 * Define a column in a edit grid. The directives cellTemplate and headerTemplate can be used to define a template to render a column.
 * This component is not rendered as it is used as a metadata for the SearchGridComponent.
 */
@Directive()
export class AbstractColumnComponent implements OnInit {
    @Input() field: string;
    @Input() columnName: string;
    @Input() title: string;
    @Input() placeholder: string;
    @Input() width: number;
    @Input() maxWidth: number;
    @Input() sortable: boolean = true;
    @Input() allowDescendingSorting: boolean = true;
    @Input() editable: boolean = false;
    @Input() linkable: boolean;
    @Input() cellRequiredGetter: (this: void, formGroup: FormGroup, dataItem: any) => boolean;
    @Input() cellEnabler: (this: void, formGroup: FormGroup, dataItem: any) => boolean;
    @Input() placeholderGetter: (this: void, dataItem: any) => any;
    @Input() elementId: string;
    @Input() hideable: boolean;
    @Input() available: boolean = true;

    private _enabled: boolean = true;

    protected editGridService: EditGridService;
    protected searchModel: SearchModel<any>;
    private labelContext: string;
    public restricted: boolean = false;

    @ContentChild(CellTemplate, {read: TemplateRef}) public contentChildCellTemplate: TemplateRef<any>;
    @ViewChild(CellTemplate, {read: TemplateRef}) public viewChildCellTemplate: TemplateRef<any>;

    @ContentChild(LinkTemplate, {read: TemplateRef}) public contentChildLinkTemplate: TemplateRef<any>;
    @ViewChild(LinkTemplate, {read: TemplateRef}) public viewChildLinkTemplate: TemplateRef<any>;

    @ContentChild(HeaderTemplate, {read: TemplateRef}) public contentChildHeaderTemplate: TemplateRef<any>;
    @ViewChild(HeaderTemplate, {read: TemplateRef}) public viewChildHeaderTemplate: TemplateRef<any>;

    @ContentChild(FooterTemplate, {read: TemplateRef}) public contentChildFooterTemplate: TemplateRef<any>;
    @ViewChild(FooterTemplate, {read: TemplateRef}) public viewChildFooterTemplate: TemplateRef<any>;

    @ContentChild(EditTemplate, {read: TemplateRef}) public contentChildEditTemplate: TemplateRef<any>;
    @ViewChild(EditTemplate, {read: TemplateRef}) public viewChildEditTemplate: TemplateRef<any>;

    /**
     * Event for action events
     * @type {EventEmitter}
     */
    @Output() itemAction: EventEmitter<ActionEvent> = new EventEmitter<ActionEvent>();

    @Output() valueChange: EventEmitter<{ value: any, dataItem: any, formGroup: FormGroup, fieldName: string, editControl: any }> = new EventEmitter();

    @Output() enabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(injector: Injector) {
        if (injector) {
            this.editGridService = injector.get(EditGridService, null);
            this.searchModel = injector.get(SearchModel, null);
            this.labelContext = injector.get(EDIT_CONTROL_LABEL_CONTEXT, null);
        }
    }

    @Input()
    public get enabled(): boolean {
        return this._enabled;
    }

    public set enabled(enabled: boolean) {
        if (this._enabled !== enabled) {
            this._enabled = enabled;
            this.enabledChange.emit(enabled);
        }
    }

    get autoClickOnRowEdit(): boolean {
        return true;
    }

    isVisible(): boolean {
        return this.available;
    }

    columnFocusHandler(focussed: boolean, columnIndex: number) {
        if (focussed && this.editGridService) {
            this.editGridService.editingColumnIndex = columnIndex;
        }
    }

    // Default behaviour is to click the edit control automatically when it is activated by a click in the cell template.
    // For some columns (e.g. checkbox), we want to disable this (autoClickOnRowEdit returns false).
    // These columns can use this method to activate the cell for click only when the cell template control itself is clicked.
    setupCellActivation(columnIndex: number, event: any) {
        if (this.editGridService) {
            this.editGridService.setupCellActivation(CellActivationMode.CLICK, columnIndex);
            event.ignoreClickOutside = true;
        }
    }

    ngOnInit(): void {
        if (!this.columnName) {
            this.columnName = this.field;
        }
        if (this.editGridService && this.editGridService.model) {
            if (!this.labelContext) {
                this.labelContext = this.editGridService.model.getResource();
            }
            if (!this.title) {
                this.title = "wbo." + this.labelContext + ".edit.column." + this.editGridService.editGrid.entityPath + "." + this.columnName;
            }
            if (!this.placeholder && !this.placeholderGetter) {
                this.placeholder = this.title;
            }
        } else if (this.searchModel) {
            if (!this.labelContext) {
                this.labelContext = this.searchModel.getResource();
            }
            if (!this.title) {
                this.title = "wbo." + this.labelContext + ".search.column." + this.columnName;
            }
        }

        if (!this.elementId && this.field) {
            this.elementId = "edit-control-" + this.field.replace(/\./g, "-");
        }
    }

    cellRequired(formGroup: FormGroup, dataItem: any): boolean {
        return this.cellRequiredGetter && this.cellRequiredGetter(formGroup, dataItem);
    }

    cellDisabled(formGroup: FormGroup, dataItem: any): boolean {
        return this.cellEnabler && !this.cellEnabler(formGroup, dataItem);
    }

    getPlaceholder(dataItem: any): string {
        return this.placeholderGetter ? this.placeholderGetter(dataItem) : this.placeholder;
    }

    controlValueChange(value: any, dataItem: any, formGroup: FormGroup): void {
        this.emitValueChange(value, dataItem, formGroup);
    }

    emitValueChange(value: any, dataItem: any, formGroup: FormGroup, fieldName?: string, editControl?: any): void {
        this.valueChange.emit({value: value, dataItem: dataItem, formGroup: formGroup, fieldName: fieldName, editControl: editControl});
    }

    /**
     * Returns a list of fields that is being edited and will be saved
     */
    editingFields(): string[] {
        return [this.field];
    }
}
